import type { FC } from 'react';
import { Suspense, useCallback, useState } from 'react';
import type { ActionFieldsOptions, ActionIndexProps } from '../../types';
import { observer } from 'mobx-react-lite';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import actionFieldsVisibility from '../../utils/actionFieldsVisibility';
import { Box, Skeleton, Typography } from '@mui/material';
import ActionMetrics from './ActionMetrics';
import ActionTypeFieldSelect from './ActionTypeFieldSelect';
import ActionValue from './ActionValue';

export const ActionCountValueField: FC<ActionIndexProps> = observer(({ indexAction, indexVariant, indexTask }) => {
  const tasksFormStore = useCurrentTasksFormStore();
  const storedActionValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant })?.action;
  const { isCountVisible } = actionFieldsVisibility(storedActionValue as string);
  const storedValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant })?.count;

  const [actionFieldsOptions, setActionFieldsOptions] = useState<ActionFieldsOptions>(() =>
    typeof storedValue === 'string' && isNaN(Number(storedValue)) ? 'metric' : 'absolute'
  );

  const handleActionTypeField = useCallback(
    (value: ActionFieldsOptions) => {
      setActionFieldsOptions(value);
      tasksFormStore.setActionFieldValue({
        indexTask,
        indexAction,
        indexVariant,
        field: 'count',
        value: '',
      });
      tasksFormStore.setActionFieldValue({
        indexTask,
        indexAction,
        indexVariant,
        field: 'unit',
        value: 'absolute',
      });
    },
    [tasksFormStore, indexVariant, indexTask, indexAction]
  );

  return isCountVisible ? (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: 37 }}>
        <Box
          sx={{
            flex: '0 0 40%',
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
            borderRight: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
          }}
        >
          <Typography variant="body2" sx={(t) => ({ pl: 2, color: t.palette.text.secondary })}>
            action count:
          </Typography>
        </Box>
        <Box
          sx={{
            flex: '0 0 90px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
            borderRight: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
          }}
        >
          <ActionTypeFieldSelect
            disabled={tasksFormStore?.propsRestriction?.disabled}
            onChange={handleActionTypeField}
            value={actionFieldsOptions}
          />
        </Box>
        <Box sx={{ flex: 1, borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}` }}>
          {actionFieldsOptions === 'metric' ? (
            <Suspense
              fallback={
                <Box sx={{ p: 0.5, display: 'flex', width: '100%', maxHeight: 36 }}>
                  <Skeleton
                    width={'100%'}
                    height={26}
                    sx={{ transform: 'translateY(0)', borderRadius: '4px', m: 0, p: 0 }}
                  />
                </Box>
              }
            >
              <ActionMetrics
                indexAction={indexAction}
                indexTask={indexTask}
                indexVariant={indexVariant}
                field="count"
              />
            </Suspense>
          ) : (
            <ActionValue
              indexAction={indexAction}
              indexTask={indexTask}
              indexVariant={indexVariant}
              field="count"
              type="number"
            />
          )}
        </Box>
      </Box>
    </>
  ) : null;
});

export default ActionCountValueField;
